<script setup lang="ts">
import { nextTick, ref } from "vue";
import GreetingBlock from "../components/GreetingBlock.vue";
import GreetingPage from "./templates/GreetingPage.vue";
import InputBox from "../components/InputBox.vue";
import TextArea from "../components/TextArea.vue";
import BlueButton from "../components/BlueButton.vue";
import { locale } from "../locale";
import { createRoom } from "../api/room";
import { set_created_room, type CreatedRoom } from "../states/room";
import { router } from "../router";
import SpinnerBtn from "../components/preview/SpinnerBtn.vue";
import HelpAlert from "../components/HelpAlert.vue";
import { authState } from "../states/auth";
import {
  combineValidators,
  getEmptyFieldValidator,
  getMaxLenValidator,
} from "../utils/validators";
import TarifRequest from "../components/TarifRequest.vue";
import SwitchItem from "../components/SwitchItem.vue";
import { adminDataSetup } from "../states/adminsState";
import { build_settings } from "../assets/custom/settings";

const meetingName = ref("");
const permanent = ref(false);
const waitRoom = ref(false);
const description = ref("");
const input = ref<typeof InputBox | null>(null);
const textarea = ref<typeof TextArea | null>(null);
const nameValidator = combineValidators([
  getEmptyFieldValidator(locale.validators.fieldnames.meetname).validator,
  getMaxLenValidator(100, locale.validators.fieldnames.meetname).validator,
]);
const descValidator = getMaxLenValidator(
  1000,
  locale.validators.fieldnames.description,
);
const create = async () => {
  try {
    meetingName.value = meetingName.value.trim();
    await new Promise((resolve) => {
      nextTick(() => resolve(null));
    });
    nameValidator.validator(meetingName.value);
    if (!nameValidator.state.value) {
      input.value?.shake();
      return;
    }
    description.value = description.value.trim();
    await new Promise((resolve) => {
      nextTick(() => resolve(null));
    });
    descValidator.validator(description.value);
    if (!descValidator.state.value) {
      textarea.value?.shake();
      return;
    }
    loading.value = true;
    const res = (await createRoom({
      name: meetingName.value,
      description: description.value,
      roomType: permanent.value ? "Permanent" : "Once",
      waitRoom: waitRoom.value,
    })) as CreatedRoom;
    set_created_room(res);
    await adminDataSetup(res.id).catch((err) => {
      router.replace({
        name: "error",
        params: {
          errtype: "bad-query",
        },
      });
      throw err
    });
    router.replace({
      name: "room",
      params: {
        roomId: res.id,
      },
    });
  } catch {
    //TODO error create Room
  }
};
const loading = ref(false);
const opened = ref(false);
const openForm = () => {
  opened.value = true;
};
const closeModal = () => {
  opened.value = false;
};
</script>
<template>
  <GreetingPage class="create-meeting">
    <GreetingBlock class="create-greeting-block" :title="locale.newmeet">
      <div class="inps-container">
        <div v-if="authState.tarif === 'FREE' && !build_settings.disable_tariff_feature" class="help-block">
          <HelpAlert>{{ locale.tarifAllert }}
            <a href="#" class="modal-btn" @click="openForm">{{
              locale.target
              }}</a>.
          </HelpAlert>
        </div>
        <InputBox ref="input" v-model="meetingName" :validator="nameValidator.validator" :title="locale.name"
          :placeholder="locale.passname" />
        <TextArea v-model="description" ref="textarea" :title="locale.description" :placeholder="locale.passdescription"
          :validator="descValidator.validator" />
        <div class="checkbox-wrapper">
          <SwitchItem v-model="permanent" :text="locale.permanentLinkSwitchText" />
        </div>
        <div class="checkbox-wrapper">
          <SwitchItem v-model="waitRoom" :text="locale.WaitRoomSwitchText" />
        </div>
        <div class="controls" v-if="!loading">
          <BlueButton @click="create">{{ locale.create }}</BlueButton>
        </div>
        <div class="preload" v-else>
          <span class="text">
            {{ locale.roomCreateLoading }}
          </span>
          <SpinnerBtn />
        </div>
      </div>
    </GreetingBlock>
    <TarifRequest v-if="opened && !build_settings.disable_tariff_feature" url="https://forms.amocrm.ru/rvzdmzd" @close="closeModal"></TarifRequest>
  </GreetingPage>
</template>
<style scoped>
.inps-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
}

.create-greeting-block {
  min-width: 555px;
}

.modal-btn {
  color: #0099c0;
  text-decoration: none;
  cursor: pointer;
}

.modal-btn:hover {
  color: #0099c0;
  text-decoration: none;
}

.modal-btn:active {
  color: #0099c0;
  text-decoration: none;
}

.controls {
  padding: 0 12px 0 0;
  display: flex;
  flex-direction: row-reverse;
  padding-top: 12px;
}

.preload {
  width: 100%;
  display: flex;
  height: 36px;
  padding: 0 12px 0 60px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.preload .text {
  text-align: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  color: #747474;
  flex-grow: 1;
}

.help-block {
  padding: 12px;
}

.checkbox-wrapper {
  padding: 0 12px;
}
</style>
